/**
 * IconSelector - Reusable icon picker component
 *
 * Displays a grid of icons that can be selected. Supports controlled mode
 * with value/onChange pattern.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface IconSelectorProps {
  /** Array of icons (emoji strings) to display */
  icons: string[];
  /** Currently selected icon */
  value: string;
  /** Callback when an icon is selected */
  onChange: (icon: string) => void;
  /** Optional label for the field */
  label?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Custom class name for the container */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export default function IconSelector({ icons, value, onChange, label, disabled = false, className }: IconSelectorProps): ReactElement {
  return (
    <div className={className}>
      {label && <label className="text-silver mb-1.5 block text-sm font-medium">{label}</label>}
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label || 'Icon selection'}>
        {icons.map((icon) => {
          const isSelected = value === icon;
          return (
            <button
              key={icon}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`Select icon ${icon}`}
              onClick={() => onChange(icon)}
              disabled={disabled}
              className={clsx(
                'flex h-10 w-10 items-center justify-center rounded-lg text-lg transition-colors',
                isSelected ? 'bg-aether ring-aether/50 ring-2' : 'bg-slate hover:bg-storm disabled:opacity-50',
              )}
            >
              {icon}
            </button>
          );
        })}
      </div>
    </div>
  );
}
